import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Pencil } from "lucide-react";
import { toast } from "sonner";
import { TopBar } from "@/components/layout/AppShell";
import { Money } from "@/components/shared/Money";
import { Bar, Eyebrow, Hint } from "@/components/shared/Primitives";
import { Mascot } from "@/components/shared/Mascot";
import { Reminders } from "@/features/agora/Reminders";
import { AISuggestions } from "@/features/assistente/AISuggestions";
import NextStepCard from "@/features/journey/NextStepCard";
import { useCouple } from "@/hooks/useCouple";
import { useMonthSummary } from "@/hooks/useSummary";
import { useCollection } from "@/hooks/useCollection";
import { fileUrl } from "@/lib/http";
import { greeting, monthKey, monthName, pct, shortDate, todayISO } from "@/lib/format";
import { QuickEntrySheet } from "@/components/layout/QuickEntrySheet";
import { InstallBanner } from "@/components/shared/Install";
import { AllowanceCard } from "@/features/dinheiro/AllowanceCard";

export default function AgoraPage() {
  const { couple, me, partner, labels } = useCouple();
  const month = monthKey();
  const { data: s } = useMonthSummary(month);
  const photo = couple?.photo_file_id ? fileUrl(couple.photo_file_id, true) : null;

  return (
    <div data-testid="agora-page">
      <TopBar eyebrow={`${monthName(month)} · ${couple?.name || ""}`} />
      <section className="flex items-center gap-4 rise">
        <Link to="/perfil" data-testid="couple-photo-link" className="shrink-0 w-16 h-16 rounded-full overflow-hidden border border-[var(--line)] bg-[var(--cream-surface)] grain relative">
          {photo && <img src={photo} alt="" className="w-full h-full object-cover" />}
        </Link>
        <div>
          <h1 className="serif text-3xl leading-tight text-[var(--coffee)]">
            {greeting()}, {me?.name}{partner ? ` & ${partner.name}` : ""}.
          </h1>
          <p className="hand text-xl text-[var(--coral)] mt-0.5">{labels.agora_title}</p>
        </div>
      </section>

      {s && <Reminders s={s} />}
      <NextStepCard />
      <AISuggestions />
      {!couple?.raiox_completed_at && <RaioXBanner />}
      {s && <MonthStrip s={s} />}
      {s && <OpenBills bills={s.open_bills} month={month} />}
      {s && <FeaturedPlan goal={s.featured_goal} />}
      <GoodThing memory={s?.latest_memory} />
      <InstallBanner className="mt-8 rise rise-5" />
    </div>
  );
}

const RaioXBanner = () => (
  <Link to="/raio-x" data-testid="raio-x-start-button" className="paper-dark block mt-8 p-5 relative overflow-hidden rise rise-1 transition-transform active:scale-[0.99]">
    <p className="condensed text-3xl leading-none">Raio-X da nossa vida</p>
    <p className="text-sm text-[var(--cream)]/75 mt-2 max-w-[70%] leading-relaxed">Cinco etapas, sem julgamento. No fim, a gente vê de onde parte.</p>
    <span className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-[var(--coral)]">começar <ArrowRight size={16} /></span>
    <Mascot pose="thinking" size={92} className="absolute right-3 bottom-3 mix-blend-normal rounded-full border-[3px] border-[var(--cream)] rotate-[4deg]" />
  </Link>
);

const MonthStrip = ({ s }) => (
  <section className="mt-8 rise rise-2" data-testid="month-strip">
    <div className="grid grid-cols-3 gap-2">
      {[["entrou", s.entrou, "var(--teal)"], ["usamos", s.usamos, "var(--coffee)"], ["futuro", s.futuro, "var(--coral)"]].map(([k, v, c]) => (
        <div key={k} className="paper p-4">
          <Eyebrow>{k}</Eyebrow>
          <Money value={v} className="block text-xl font-bold mt-2 leading-none" testId={`month-${k}`} />
          <div className="h-1 w-8 rounded-full mt-3" style={{ background: c }} />
        </div>
      ))}
    </div>
    {s.estimated ? (
      <Hint className="mt-3">Ainda sem lançamentos este mês — mostrando o combinado do Raio-X. Lançar é opcional.</Hint>
    ) : (
      <p className="text-xs text-[var(--coffee-muted)] mt-3">
        {s.tx_count} lançamento{s.tx_count === 1 ? "" : "s"} · sobra do mês <Money value={s.saldo} className="font-semibold text-[var(--coffee)]" testId="month-saldo" />
      </p>
    )}
    <AllowanceCard allowance={s.allowance} compact />
  </section>
);

const OpenBills = ({ bills, month }) => {
  const tx = useCollection("transactions", { month });
  const { me, partner } = useCouple();
  const pay = (b) => tx.create.mutate(
    { type: "gasto", amount: b.amount, date: todayISO(), category_id: b.category_id, owner: b.owner, responsible_id: b.owner === "meu" ? me?.id : b.owner === "seu" ? partner?.id : b.responsible_id, note: b.label, recurring_id: b.id },
    { onSuccess: () => toast.success(`${b.label} marcado como pago`) }
  );
  if (!bills?.length) return null;
  return (
    <section className="mt-8 rise rise-3" data-testid="open-bills">
      <div className="flex items-center justify-between">
        <Eyebrow>em aberto este mês</Eyebrow>
        <Link to="/dinheiro?tab=contas" className="text-xs font-semibold text-[var(--teal)] min-h-[44px] flex items-center" data-testid="agora-calendar-link">todas as contas →</Link>
      </div>
      <ul className="mt-3 divide-y divide-[var(--line)] paper px-4">
        {bills.map((b) => (
          <li key={b.id} className="flex items-center justify-between py-3 gap-3" data-testid={`open-bill-${b.id}`}>
            <div className="min-w-0">
              <p className="font-semibold text-sm truncate">{b.label}</p>
              <p className="text-xs text-[var(--coffee-muted)] num">{b.days_left < 0 ? `passou do dia ${b.due_day}` : `vence dia ${b.due_day}`}</p>
            </div>
            <Money value={b.amount} className="font-bold text-sm shrink-0" />
            <button className="w-11 h-11 rounded-full border border-[var(--line)] flex items-center justify-center text-[var(--teal)] hover:bg-[var(--teal-light)] transition-colors shrink-0"
              onClick={() => pay(b)} aria-label="marcar como pago" data-testid={`open-bill-pay-${b.id}`}>
              <Check size={18} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
};

const FeaturedPlan = ({ goal }) => {
  const [aporte, setAporte] = useState(false);
  if (!goal) {
    return (
      <Link to="/planos" data-testid="featured-plan-empty" className="mt-8 block paper p-5 rise rise-4">
        <Eyebrow>plano em destaque</Eyebrow>
        <p className="serif text-xl mt-2">Nenhum plano ainda. Que tal um primeiro?</p>
      </Link>
    );
  }
  const cover = goal.cover_file_id ? fileUrl(goal.cover_file_id, true) : goal.cover_url;
  const remaining = Math.max(0, goal.target - goal.saved);
  const monthsLeft = goal.deadline ? Math.max(1, Math.round((new Date(goal.deadline) - new Date()) / (1000 * 60 * 60 * 24 * 30))) : null;
  const pace = monthsLeft ? remaining / monthsLeft : null;
  const thisMonthAportes = goal.saved_this_month || 0;
  return (
    <section className="mt-8 rise rise-4" data-testid="featured-plan">
      <Link to={`/planos/${goal.id}`} data-testid="featured-plan-link" className="block paper overflow-hidden transition-transform active:scale-[0.99]">
        <div className="h-36 bg-[var(--cream-surface)] relative">
          {cover && <img src={cover} alt="" className="w-full h-full object-cover" />}
          <span className="absolute top-3 left-3 bg-[var(--cream)]/90 backdrop-blur px-2.5 py-1 rounded-full eyebrow">plano em destaque</span>
        </div>
        <div className="p-4">
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="serif text-2xl leading-none">{goal.title}</h3>
            <span className="num font-bold text-sm">{pct(goal.progress)}</span>
          </div>
          <Bar ratio={goal.progress} color="var(--coral)" className="mt-3" />
          <p className="text-xs text-[var(--coffee-muted)] mt-2"><Money value={goal.saved} /> de <Money value={goal.target} /></p>
        </div>
      </Link>
      <div className="paper mt-2 p-4 flex items-center gap-3" data-testid="featured-plan-pace">
        <div className="flex-1 min-w-0">
          {pace != null ? (
            <>
              <Eyebrow>aporte sugerido este mês</Eyebrow>
              <p className="text-sm mt-1"><Money value={pace} className="font-bold text-base" testId="featured-plan-pace-value" /> <span className="text-[var(--coffee-muted)]">· {monthsLeft} {monthsLeft === 1 ? "mês" : "meses"} até {shortDate(goal.deadline)}</span></p>
              {thisMonthAportes > 0 && <p className="text-xs text-[var(--teal)] mt-0.5">já foram <Money value={thisMonthAportes} className="font-semibold" /> este mês</p>}
            </>
          ) : (
            <>
              <Eyebrow>ritmo</Eyebrow>
              <p className="text-sm mt-1 text-[var(--coffee-muted)]">Sem data ainda. <Link to={`/planos/${goal.id}`} className="font-semibold text-[var(--teal)] underline underline-offset-2" data-testid="featured-plan-set-deadline">Definir</Link> pra ver quanto guardar por mês.</p>
            </>
          )}
        </div>
        <button className="btn-primary h-11 px-4 shrink-0" onClick={() => setAporte(true)} data-testid="featured-plan-aporte-button">aportar</button>
      </div>
      <QuickEntrySheet open={aporte} onOpenChange={setAporte} defaultType="aporte" defaultGoalId={goal.id} />
    </section>
  );
};

const GoodThing = ({ memory }) => {
  const { couple, updateCouple } = useCouple();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(couple?.good_thing || "");
  const save = () => { updateCouple.mutate({ good_thing: text }); setEditing(false); };
  const value = couple?.good_thing || memory?.title;
  return (
    <section className="mt-8 relative paper p-5 pt-6 rise rise-5" data-testid="good-thing">
      <span className="washi teal" />
      <Eyebrow>uma coisa boa do mês</Eyebrow>
      {editing ? (
        <div className="mt-2 flex gap-2">
          <input className="field" autoFocus value={text} onChange={(e) => setText(e.target.value)} data-testid="good-thing-input" placeholder="ex.: jantar na varanda com chuva" />
          <button className="btn-primary px-4" onClick={save} data-testid="good-thing-save-button"><Check size={18} /></button>
        </div>
      ) : (
        <button className="mt-2 text-left w-full flex items-start justify-between gap-3" onClick={() => { setText(couple?.good_thing || ""); setEditing(true); }} data-testid="good-thing-edit-button">
          <p className={value ? "hand text-2xl text-[var(--coffee)] leading-tight" : "text-sm text-[var(--coffee-muted)] italic"}>
            {value || "Ainda em branco. O que valeu a pena até aqui?"}
          </p>
          <Pencil size={16} className="text-[var(--coffee-muted)] mt-1 shrink-0" />
        </button>
      )}
    </section>
  );
};
